'use client';
import React, { useState, useEffect, useRef } from 'react';
import PropTypes from 'prop-types';
import { IconButton } from '@ui/button';
import { Container, ButtonContainer, Content } from './styles';

export const ExpandingContainer = ({
  children,
  height = 350,
  width = 600,
  expandedWidth,
  onMobileClick,
}) => {
  const [isExpanded, setIsExpanded] = useState(false);
  const [isMobile, setIsMobile] = useState(false);

  const containerRef = useRef(null);

  useEffect(() => {
    //handle escape key to close container
    const handleKeyDown = event => {
      if (event.key === 'Escape') setIsExpanded(false);
    };
    document.addEventListener('keydown', handleKeyDown);

    //handle clicking out to close container
    const handleClickOutside = event => {
      if (
        containerRef.current &&
        !containerRef.current.contains(event.target)
      ) {
        setIsExpanded(false);
      }
    };

    document.addEventListener('mousedown', handleClickOutside);

    //handle resize screen
    const mediaQuery = window.matchMedia(`(max-width: 768px)`);
    setIsMobile(mediaQuery.matches);
    const handler = event => setIsMobile(event.matches);
    mediaQuery.addEventListener('change', handler);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
      document.removeEventListener('mousedown', handleClickOutside);
      mediaQuery.removeEventListener('change', handler);
    };
  }, []);

  const handleButtonClick = event => {
    event.stopPropagation();
    if (isMobile && onMobileClick) {
      onMobileClick();
    } else {
      setIsExpanded(!isExpanded);
    }
  };

  return (
    <Container
      ref={containerRef}
      $height={height}
      $width={width}
      $expandedWidth={expandedWidth}
      $isExpanded={isExpanded}
      onClick={handleButtonClick}
    >
      <ButtonContainer>
        <IconButton
          aria-label={isExpanded ? 'Collapse content' : 'Expand content'}
          icon={
            isExpanded
              ? {
                  iconName: 'close',
                  size: '24px',
                  alt: 'close-button',
                  id: 'close-button',
                }
              : {
                  iconName: 'plus',
                  size: '24px',
                  alt: 'open-button',
                  id: 'open-button',
                }
          }
          type="button"
          variant="secondary"
          id={isExpanded ? 'close-button' : 'open-button'}
        />
      </ButtonContainer>
      <Content $isExpanded={isExpanded}>{children}</Content>
    </Container>
  );
};

ExpandingContainer.displayName = 'ExpandingContainer';

ExpandingContainer.propTypes = {
  children: PropTypes.node.isRequired,
  height: PropTypes.number,
  width: PropTypes.number,
  expandedWidth: PropTypes.number,
  onMobileClick: PropTypes.func,
};
